import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { MagnifyingGlass, ArrowRight, Tag, IdentificationCard } from "@phosphor-icons/react";

const CATEGORIES = ["All", "Identity", "Business", "Legal", "Utility", "Finance", "Other"];

export default function PublicServices() {
  const [params] = useSearchParams();
  const [popular, setPopular] = useState([]);
  const [published, setPublished] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/public/services").then((r) => {
      setPopular(r.data.popular || []);
      setPublished(r.data.published || []);
    }).catch(() => {});
  }, []);

  const list = useMemo(() => {
    const term = q.toLowerCase().trim();
    return popular.filter((s) => {
      if (cat !== "All" && s.category !== cat) return false;
      if (!term) return true;
      return s.name.toLowerCase().includes(term) || s.category.toLowerCase().includes(term) || (s.hint || "").toLowerCase().includes(term);
    });
  }, [popular, q, cat]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="public-services">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#1e3a8a] mb-1.5">Service Catalogue</div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Every service, one place.</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Browse our full range of government &amp; business services. Apply online or visit any of our branches.</p>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="flex items-center gap-2 flex-1 min-w-[240px] max-w-md bg-white border border-slate-200 rounded-full pl-4 pr-2 py-1.5">
          <MagnifyingGlass size={16} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 py-1.5 outline-none bg-transparent text-sm" placeholder="Search e.g. PAN, Aadhaar, GST…" data-testid="services-search" />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="services-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`cat-${c.toLowerCase()}`}
              className={`px-3 h-9 rounded-full text-xs font-semibold border transition-colors ${cat === c ? "bg-[#1e3a8a] text-white border-[#1e3a8a]" : "bg-white text-slate-700 border-slate-200 hover:border-[#1e3a8a]"}`}
            >{c}</button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {list.map((s) => (
          <Link key={s.slug} to={`/apply?service=${s.slug}`} data-testid={`service-${s.slug}`}
            className="group bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#1e3a8a] hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-[#1e3a8a]">{s.category}</span>
            </div>
            <div className="font-semibold text-slate-900 mb-1 leading-tight">{s.name}</div>
            <div className="text-[11px] text-slate-500 leading-snug mb-3">{s.hint}</div>
            <div className="text-xs text-[#1e3a8a] font-semibold inline-flex items-center gap-1">Apply now <ArrowRight size={12} /></div>
          </Link>
        ))}
        {list.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-400 text-sm">No services match "{q}"</div>
        )}
      </div>

      {published.length > 0 && (
        <>
          <div className="mt-14 mb-4 flex items-center gap-2">
            <Tag size={16} className="text-[#ea580c]" />
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Published rates</h2>
          </div>
          <div className="overflow-x-auto bg-white rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-4 py-3">Service</th><th className="text-left px-4 py-3">Category</th><th className="text-right px-4 py-3">Govt Fee</th><th className="text-right px-4 py-3">Service Charge</th><th className="text-right px-4 py-3">Total</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {published.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                    <td className="px-4 py-3 text-slate-600">{p.category}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{p.govt_fee.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono">₹{p.service_charge.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-[#1e3a8a]">₹{p.total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
